import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { ErrorMessage } from '../../../../../../base/components/Forms/ErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { __t } from '../../../../../../base/utils/t';
import { Icon } from '../../../../../../base/components/Icon';
import { Speciality } from '../../../../../../base/interfaces/config';
import { vacancyFormActions } from '../../../redux/slices/vacancyFormSlice';
import { highlightMatchingText } from '../../../../../../base/utils';
import { VACANCY_MAX_SPECIALITY_COUNT } from '../../../constants';

import './styles.scss';
import { appActions } from '../../../../../../base/redux/slices/appSlice';
import { Loading } from '../../../../../../base/enums';
import { employerAppActions } from '../../../../../redux/slices/employerAppSlice';

export const SpecialityAutocompleteMobile = () => {
    const dispatch = useAppDispatch();
    const { loading } = useAppSelector((state) => state.app);
    const {
        fields: { specialities: selectedOptions, areas },
        errors,
    } = useAppSelector((state) => state.vacancyForm);
    const options = useAppSelector((state) => state.employerApp.specialitySearchOptions);
    const [inputValue, setInputValue] = useState('');
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const isLoading = useMemo(() => loading === Loading.SpecialityAutocomplete, [loading]);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (showMobileSearch && inputRef.current && searchContainerRef.current) {
            searchContainerRef.current.style.height = `${window.innerHeight}px`;
            inputRef.current.focus();
        }
    }, [showMobileSearch, inputRef, searchContainerRef]);

    const loader = useMemo(
        () =>
            isLoading ? <CircularProgress size={16} className='app-speciality-autocomplete-mobile__loader' /> : null,
        [isLoading],
    );

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart();
        dispatch(employerAppActions.specialitySearchSuccess(null));
        setInputValue(value);
        if (value) {
            dispatch(employerAppActions.specialitySearchRequest(value));
        }
    };

    const backClickHandler = () => {
        setShowMobileSearch(false);
        setInputValue('');
        dispatch(employerAppActions.specialitySearchSuccess(null));
        dispatch(appActions.setLoading(null));
    };

    const optionSelectHandler = (option: Speciality) => {
        dispatch(
            vacancyFormActions.setField({
                id: 'specialities',
                value: [...selectedOptions, option],
                errorGroupName: 'specialityIds',
            }),
        );
        dispatch(
            vacancyFormActions.setField({
                id: 'areas',
                value: [...areas, option.area],
            }),
        );
        dispatch(employerAppActions.specialitySearchSuccess(null));
        setInputValue('');
        setShowMobileSearch(false);
    };

    const onChipDelete = (e: React.MouseEvent<HTMLElement>, id: number) => {
        e.stopPropagation();
        const index = selectedOptions.findIndex((option) => option.id === id);
        const filteredSpecialities = selectedOptions.filter((option) => option.id !== id);
        dispatch(
            vacancyFormActions.setField({
                id: 'specialities',
                value: filteredSpecialities,
                errorGroupName: 'specialityIds',
            }),
        );
        dispatch(
            vacancyFormActions.setField({
                id: 'areas',
                value: areas.filter((v, i) => i !== index),
            }),
        );
    };

    const showMobileSearchHandler = () => {
        if (selectedOptions.length < VACANCY_MAX_SPECIALITY_COUNT) {
            setShowMobileSearch(true);
        }
    };

    const searchOptions = useMemo(() => {
        if (!options) {
            return null;
        }

        if (options && !options.length && !isLoading) {
            return (
                <ul className='app-speciality-autocomplete-mobile__options-list'>
                    <li className='app-speciality-autocomplete-mobile__option-item'>
                        <span>{__t('По вашему запросу ничего не найдено')}</span>
                    </li>
                </ul>
            );
        }

        let optionsToRender = options;
        if (selectedOptions.length) {
            const selectedOptionsIds = selectedOptions.map((s) => s.id);
            optionsToRender = options.filter((i) => !selectedOptionsIds.includes(i.id));
        }

        return (
            <ul className='app-speciality-autocomplete-mobile__options-list'>
                {optionsToRender &&
                    optionsToRender.map((option) => (
                        <li
                            key={option.id}
                            className='app-speciality-autocomplete-mobile__option-item'
                            onClick={() => optionSelectHandler(option)}
                        >
                            <span className='app-speciality-autocomplete-mobile__option-title'>
                                {highlightMatchingText(option.name, inputValue)}
                            </span>
                            <span className='app-speciality-autocomplete-mobile__option-additional-text'>
                                {option.area.name}
                            </span>
                        </li>
                    ))}
            </ul>
        );
    }, [options, selectedOptions, inputValue, isLoading, showMobileSearch]);

    const renderSelectedChips = useMemo(
        () =>
            selectedOptions.map((option) => (
                <div className='selected-speciality-chip' key={option.id}>
                    <span className='selected-speciality-chip__value'>{option.name}</span>
                    <div
                        className='selected-speciality-chip__remove-button'
                        onClick={(e: React.MouseEvent<HTMLElement>) => onChipDelete(e, option.id)}
                    >
                        <Icon icon='xmark-regular' size='10' />
                    </div>
                </div>
            )),
        [selectedOptions],
    );

    return (
        <div className='app-speciality-autocomplete-mobile'>
            <div className='app-speciality-autocomplete-mobile__preview' onClick={showMobileSearchHandler}>
                {renderSelectedChips}

                {!selectedOptions.length ? (
                    <span className='app-speciality-autocomplete-mobile__preview-placeholder'>
                        {__t('К примеру, «Работник склада»')}
                    </span>
                ) : null}
            </div>

            <div className='app-speciality-autocomplete__additional-data-wrap'>
                <ErrorMessage errors={errors?.specialityIds} />
                <div className='app-speciality-autocomplete__speciality-count'>
                    {selectedOptions.length}/{VACANCY_MAX_SPECIALITY_COUNT}
                </div>
            </div>

            {showMobileSearch && (
                <div className='app-speciality-autocomplete-mobile__container' ref={searchContainerRef}>
                    <div className='app-speciality-autocomplete-mobile__header'>
                        <div className='app-speciality-autocomplete-mobile__header-icon' onClick={backClickHandler}>
                            <Icon icon='chevron-left' color='white' size='20' />
                        </div>
                        <div className='app-speciality-autocomplete-mobile__input-container'>
                            <input
                                type='text'
                                value={inputValue}
                                onChange={inputChangeHandler}
                                className='app-speciality-autocomplete-mobile__input-component'
                                placeholder={__t('К примеру, «Работник склада»')}
                                ref={inputRef}
                                autoComplete='off'
                            />
                            {loader}
                        </div>
                    </div>
                    {searchOptions}
                </div>
            )}
        </div>
    );
};
